/**
 * @description API service for payment sessions and stored card management.
 * Portal-specific version — uses customer-portal's apiClient.
 *
 * Key features:
 * - Payment session creation (Order → HPF Session)
 * - Card vaulting after HPF capture
 * - Stored card list, get, set-default, remove
 * - HPF client config retrieval (publicKey, scriptUrl)
 *
 * @dependencies
 * - apiClient: Customer portal's Axios instance
 *
 * @notes
 * - Identical API surface to frontend/src/services/api/paymentSessionService.js
 * - Only difference is the apiClient import (portal vs. frontend)
 * - Amount is always in cents (integer)
 */

import apiClient from './apiClient';

const paymentSessionService = {
  /**
   * Get gateway client config for HPF SDK initialization.
   * @returns {Promise<object>} - { publicKey, hpfScriptUrl, environment, gatewayType, features }
   */
  getClientConfig: async () => {
    const response = await apiClient.get('/payment-sessions/client-config');
    return response.data;
  },

  /**
   * Get gateway client config via portal endpoint (no auth required).
   * @param {string} customerPublicId - Customer public UUID
   * @returns {Promise<object>} - { publicKey, hpfScriptUrl, environment, gatewayType, features }
   */
  getPortalClientConfig: async (customerPublicId) => {
    const response = await apiClient.get(`/portal/customer/${customerPublicId}/client-config`);
    return response.data;
  },

  /**
   * Create a payment session for HPF card capture (merchant-authenticated).
   * @param {object} data - { amount (cents), currency, description }
   * @returns {Promise<object>} - { sessionId, sessionUrl, orderId, expiresAt }
   */
  createPaymentSession: async (data) => {
    const response = await apiClient.post('/payment-sessions', data);
    return response.data;
  },

  /**
   * Create an EPG Order + HPF Session via portal endpoint (no auth required).
   * Portal equivalent of createPaymentSession — uses customerPublicId for scoping.
   * @param {string} customerPublicId - Customer public UUID
   * @param {object} data - { amount (cents), currency, description }
   * @returns {Promise<object>} - { sessionId, sessionUrl, orderId, expiresAt }
   */
  createPortalEpgSession: async (customerPublicId, data) => {
    const response = await apiClient.post(`/portal/customer/${customerPublicId}/epg-session`, data);
    return response.data;
  },

  /**
   * Vault a HostedCard from HPF into a StoredCard.
   * @param {string} sessionId - Payment session ID
   * @param {object} data - { hostedCardId, customerId, customerData }
   * @returns {Promise<object>} - { storedCard: { id, lastFour, cardBrand, ... } }
   */
  vaultCard: async (sessionId, data) => {
    const response = await apiClient.post(`/payment-sessions/${sessionId}/vault`, data);
    return response.data;
  },

  /**
   * List all stored cards for a customer.
   * @param {number} customerId - Customer integer ID
   * @returns {Promise<object>} - { cards: Array<StoredCard> }
   */
  getCustomerCards: async (customerId) => {
    const response = await apiClient.get(`/stored-cards/customer/${customerId}`);
    return response.data;
  },

  /**
   * Get a single stored card by ID.
   * @param {string} cardId - Card UUID
   * @returns {Promise<object>} - { card: StoredCard }
   */
  getCard: async (cardId) => {
    const response = await apiClient.get(`/stored-cards/${cardId}`);
    return response.data;
  },

  /**
   * Set a card as the customer's default.
   * @param {string} cardId - Card UUID
   * @param {number} customerId - Customer integer ID
   * @returns {Promise<object>} - { card: StoredCard }
   */
  setDefaultCard: async (cardId, customerId) => {
    const response = await apiClient.put(`/stored-cards/${cardId}/default`, { customerId });
    return response.data;
  },

  /**
   * Remove a stored card (gateway delete + local deactivation).
   * @param {string} cardId - Card UUID
   * @returns {Promise<object>} - { message, card: StoredCard }
   */
  removeCard: async (cardId) => {
    const response = await apiClient.delete(`/stored-cards/${cardId}`);
    return response.data;
  }
};

export default paymentSessionService;
